import React, { useEffect, useState } from "react";
import useSessionExpiryWarning from "../hooks/useSessionExpiryWarning.js";
import { setAccessToken } from "../Util/authStorage.js";
import ConfirmModal from "./ConfirmModal.js";

/**
 * Session-expiry UX. Sessions are absolute (a refresh never extends them), so
 * the honest offer is "save now, then sign in again" — not "stay signed in".
 *
 * <SessionBadge />  — the Profile menu label; shows the countdown in the last
 *                     5 minutes. Owns the 1 s tick, so only this tiny label
 *                     re-renders, not the whole Nav.
 * <SessionExpiryDialog onSignInAgain /> — interrupts once when the 5-minute
 *                     warning starts ("Keep working" dismisses it), and again
 *                     when the session has ended. The ended dialog can't be
 *                     dismissed and drops the access token at once, so no
 *                     further request goes out on an expired session.
 */
export function SessionBadge({ fallback = "Profile" }) {
    const countdown = useSessionExpiryWarning();
    if (!countdown) return fallback;
    const ended = countdown === "Session Expired";
    return (
        <span className="session-badge" title={ended ? "Your session has ended" : "Time left in this session"}>
            <i className={`bi ${ended ? "bi-lock" : "bi-hourglass-split"}`} aria-hidden="true" /> {countdown}
        </span>
    );
}

export function SessionExpiryDialog({ onSignInAgain }) {
    const countdown = useSessionExpiryWarning();
    const ended = countdown === "Session Expired";
    const warning = !!countdown && !ended;
    // The 5-minute warning was acknowledged with "Keep working"
    const [warnDismissed, setWarnDismissed] = useState(false);

    useEffect(() => {
        if (!countdown) setWarnDismissed(false); // new session → warn again next time
    }, [countdown]);

    useEffect(() => {
        if (ended) setAccessToken(null);
    }, [ended]);

    const open = ended || (warning && !warnDismissed);

    return (
        <ConfirmModal
            isOpen={open}
            icon="lock"
            title={ended ? "Your session has ended" : "Your session ends soon"}
            message={ended
                ? "Sign in again to keep working. Drafts on most forms are kept."
                : `About ${countdown} left. Save what you're working on — after that you'll need to sign in again. Drafts on most forms are kept.`}
            confirmText="Sign in again now"
            cancelText="Keep working"
            dismissible={!ended}
            confirmVariant="primary"
            onConfirm={() => onSignInAgain?.()}
            onCancel={() => setWarnDismissed(true)}
        />
    );
}
